import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import LoadingSkeleton from '@applied/dragonfruit/src/components/LoadingSkeleton/LoadingSkeleton';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQuery } from '@tanstack/react-query';
import { formatName } from 'api/parsing';

import { ImageWithBackups } from 'components/common/ImageWithBackups';
import { getIcon, WebOfferIcon } from 'components/WebOfferPage/icon_requests';
import { useWebOffer } from 'components/WebOfferPage/web_offer_context';

import './hero-section.scss';

import PlaceholderHeadshot from 'assets/placeholder_headshot.svg';

export const HeroSection = (): JSX.Element => {
  const {
    candidate: { firstName, lastName, profileImageUrl },
  } = useWebOffer();
  const history = useHistory();
  const [hasLoadedConfetti, setHasLoadedConfetti] = useState(false);

  const { data: confettiAnimationResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.CONFETTI_ANIMATION],
    queryFn: () => getIcon(WebOfferIcon.CONFETTI_ANIMATION, history),
    enabled: !hasLoadedConfetti,
  });

  useEffect(() => {
    if (confettiAnimationResponse) {
      setHasLoadedConfetti(true);
    }
  }, [confettiAnimationResponse, setHasLoadedConfetti]);

  const loadingComponent = (
    <Flex direction="column" gap="sm" alignment="center" justify="center">
      <LoadingSkeleton width="600px" height="75px" />
      <LoadingSkeleton width="400px" height="75px" />
    </Flex>
  );

  const formattedName = firstName && lastName ? formatName(`${firstName} ${lastName}`) : null;

  return (
    <Flex className="hero-section" direction="column" gap="md" alignment="center" justify="center">
      {formattedName ? (
        <>
          <Flex
            className="hero-section-content"
            direction="column"
            gap="md"
            alignment="center"
            justify="center"
          >
            {profileImageUrl && (
              <ImageWithBackups
                className="profile-image"
                imageSources={[profileImageUrl]}
                defaultImage={PlaceholderHeadshot}
              />
            )}
            <Typography variant="header">Congratulations, {formattedName}!</Typography>
            <Typography variant="body">You have an offer from Applied Intuition.</Typography>
          </Flex>
          {confettiAnimationResponse?.iconUrl && (
            <img className="confetti-animation" src={confettiAnimationResponse.iconUrl} />
          )}
        </>
      ) : (
        loadingComponent
      )}
    </Flex>
  );
};
